// +----------------------------------------------------------------------
// | CRMEB [ CRMEB赋能开发者，助力企业发展 ]
// +----------------------------------------------------------------------
// | Copyright (c) 2016~2025 https://www.crmeb.com All rights reserved.
// +----------------------------------------------------------------------
// | Licensed CRMEB并不是自由软件，未经许可不能去掉CRMEB相关版权
// +----------------------------------------------------------------------
// | Author: CRMEB Team <admin@crmeb.com>
// +----------------------------------------------------------------------

import axios from 'axios';
import { MessageBox, Message } from 'element-ui';
import router from '@/router';
import store from '@/store';
import { getToken } from '@/utils/auth';
import SettingMer from '@/utils/settingMer';

// 全局标识：防止重复弹出 401 提示
var isReloginShowing = false;

// 防止重复提示的消息缓存
var messageQueue = new Map();
var MESSAGE_DURATION = 3000; // 消息去重时间窗口（毫秒）

/**
 * 显示消息提示（带去重功能）
 * @param {String} message - 消息内容
 * @param {String} type - 消息类型：success/warning/info/error
 * @param {Number} duration - 显示时长
 */
function showMessage(message) {
  var type = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'error';
  var duration = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 5000;
  var key = "".concat(type, "_").concat(message);
  var now = Date.now();

  // 检查是否在时间窗口内已经显示过相同消息
  if (messageQueue.has(key)) {
    var lastTime = messageQueue.get(key);
    if (now - lastTime < MESSAGE_DURATION) {
      return; // 跳过重复消息
    }
  }

  // 记录消息显示时间
  messageQueue.set(key, now);

  // 显示消息
  Message({
    message: message,
    type: type,
    duration: duration
  });

  // 清理过期的消息记录
  setTimeout(function () {
    messageQueue.delete(key);
  }, MESSAGE_DURATION);
}
var service = axios.create({
  baseURL: SettingMer.apiBaseURL,
  timeout: 60000 // 过期时间
});

// request interceptor
service.interceptors.request.use(function (config) {
  // 发送请求之前做的
  var token = !store.getters.token ? sessionStorage.getItem('token') : store.getters.token;
  if (token) {
    config.headers['Authori-zation'] = token;
  }
  if (/get/i.test(config.method)) {
    config.params = config.params || {};
    config.params.temp = Date.parse(new Date()) / 1000;
  }
  return config;
}, function (error) {
  return Promise.reject(error);
});

// response interceptor
service.interceptors.response.use(function (response) {
  var res = response.data;
  // if the custom code is not 20000, it is judged as an error.

  if (res.code === 401) {
    // to re-login
    // 防止并发请求导致多次弹窗
    if (!isReloginShowing) {
      isReloginShowing = true;
      MessageBox.confirm('无效的会话，或者登录已过期，请重新登录。', '系统提示', {
        confirmButtonText: '重新登录',
        cancelButtonText: '取消',
        type: 'warning',
        closeOnClickModal: false,
        closeOnPressEscape: false,
        showClose: false
      }).then(function () {
        isReloginShowing = false;
        store.dispatch('user/localLogout').finally(function () {
          router.push('/login');
        });
      }).catch(function () {
        isReloginShowing = false;
      });
    }
    return Promise.reject(new Error('无效的会话，或者登录已过期，请重新登录。'));
  } else if (res.code === 403) {
    if (response.config && response.config.silentBusinessError) {
      return Promise.reject({
        code: res.code,
        message: res.message || '没有权限访问。',
        businessCode: res.data && res.data.businessCode,
        currentState: res.data && res.data.currentState
      });
    }
    showMessage('没有权限访问。', 'error');
    return Promise.reject(new Error('没有权限访问。'));
  }
  if (res.code !== 200 && res.code !== 401) {
    if (response.config && response.config.silentBusinessError) {
      return Promise.reject({
        code: res.code,
        message: res.message || 'Error',
        businessCode: res.data && res.data.businessCode,
        currentState: res.data && res.data.currentState
      });
    }
    showMessage(res.message || 'Error', 'error', 5000);
    return Promise.reject(new Error(res.message || 'Error'));
  } else {
    return res.data;
  }
}, function (error) {
  var responseData = error.response && error.response.data;
  if (error.config && error.config.silentBusinessError && responseData) {
    return Promise.reject({
      code: responseData.code,
      message: responseData.message || error.message || 'Error',
      businessCode: responseData.data && responseData.data.businessCode,
      currentState: responseData.data && responseData.data.currentState
    });
  }
  // 网络错误或其他异常
  var errorMessage = error.message || '网络异常，请稍后重试';
  showMessage(errorMessage, 'error', 5000);
  return Promise.reject(error);
});
export default service;